/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-evals.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Deliberately the same vocabulary as `serving.Purpose` — a capability is "can this model do the
 * thing that purpose exists for". A separate taxonomy would let the matrix certify a capability
 * nothing consumes. **`copilot_answer` was missing from this enum until 2026-08-17 while
 * `Capability` in Python had it.** Hard rule 10 makes this file authoritative, so the spec was
 * narrower than the API it describes: `/suite` returns four entries and `bars` has four keys, and
 * the screen only rendered the fourth because the generated envelope guards do not validate items.
 * The sharper half is that `isCapabilityResult` *does* check this enum and guards `POST /runs` —
 * so the day a copilot corpus is added, a run would spend the whole corpus and then fail as a
 * schema mismatch. Nothing compares a contract enum to the Python enum it mirrors, which is
 * ADR-010's K-R3 at a new site.
 */
export type Capability = 'pii_classification' | 'rule_suggestion' | 'anomaly_explanation' | 'copilot_answer';

export const CAPABILITYS: readonly Capability[] = ['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'];

export function isCapability(value: unknown): value is Capability {
  return (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(value);
}

/**
 * `failed` is the only verdict that **gates**. `insufficient_evidence` — ran, on too few counted
 * cases to conclude. Distinct from `failed` because a model that passed 4 of 4 has not been shown
 * capable, and calling that `proven` is how a capability matrix becomes a rubber stamp. `unproven`
 * — never evaluated. Permits, and says so.
 */
export type CapabilityVerdict = 'proven' | 'failed' | 'insufficient_evidence' | 'unproven';

export const CAPABILITY_VERDICTS: readonly CapabilityVerdict[] = ['proven', 'failed', 'insufficient_evidence', 'unproven'];

export function isCapabilityVerdict(value: unknown): value is CapabilityVerdict {
  return (['proven', 'failed', 'insufficient_evidence', 'unproven'] as readonly unknown[]).includes(value);
}

/**
 * `failed` — the model answered, the validator accepted it, and the answer was wrong. A capability
 * signal. `rejected` — the validator refused the completion. Counts against the bar, reported
 * separately: a wrong answer is a model that cannot do the task, a refused one is a model that
 * cannot produce the shape, and the second is often fixable with a prompt. `unavailable` — the
 * model could not be reached. **Not counted at all.** An outage during a run must never be
 * recorded as incapability, or a model would be gated out of features it can perform with nothing
 * re-testing it. A transport failure reaches the validator as `provider_error` rather than as an
 * exception, and is mapped here — which the first implementation did not do, scoring twelve
 * outages as twelve failures.
 */
export type CaseVerdict = 'passed' | 'failed' | 'rejected' | 'unavailable';

export const CASE_VERDICTS: readonly CaseVerdict[] = ['passed', 'failed', 'rejected', 'unavailable'];

export function isCaseVerdict(value: unknown): value is CaseVerdict {
  return (['passed', 'failed', 'rejected', 'unavailable'] as readonly unknown[]).includes(value);
}

export interface CaseRecord {
  readonly case_id: string;
  readonly verdict: CaseVerdict;
  /**
   * Host-written, and **never the model's answer**. A stored eval result is read on a screen, and
   * echoing a failed model's output would put unvalidated text in front of a person by a side
   * door.
   */
  readonly reason: string;
}

export function isCaseRecord(value: unknown): value is CaseRecord {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['case_id'] === 'string' &&
    (['passed', 'failed', 'rejected', 'unavailable'] as readonly unknown[]).includes(candidate['verdict']) &&
    typeof candidate['reason'] === 'string'
  );
}

export interface CapabilityResult {
  readonly model: string;
  readonly capability: Capability;
  /**
   * On every row, because a bar means nothing without the corpus it was measured on. "80% on v1"
   * and "80% on v2" are different facts.
   */
  readonly suite_version: string;
  readonly cases_run: number;
  readonly passed: number;
  readonly failed: number;
  readonly rejected: number;
  readonly unavailable: number;
  /** passed + failed + rejected. **Excludes unavailable.** */
  readonly counted: number;
  /** Null when nothing was counted — never 0.0, which would read as "failed everything". */
  readonly pass_rate?: number | null;
  readonly verdict: CapabilityVerdict;
  /**
   * True only for `failed`. Derived from the counts against a bar that lives in code and is **not
   * stored** — so raising a bar re-derives every historical verdict from evidence that never
   * moved. Names what the matrix *would* withhold. This paragraph read "No feature consults it
   * yet" while the description at the top of the document had been corrected to say the gate is
   * wired — two paragraphs of one contract disagreeing about whether a control exists, and the
   * stale one was the field description a client reads. `EvalBackedPurposeGate` consults the same
   * evidence before every completion.
   */
  readonly gates: boolean;
}

export function isCapabilityResult(value: unknown): value is CapabilityResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['model'] === 'string' &&
    (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(candidate['capability']) &&
    typeof candidate['suite_version'] === 'string' &&
    typeof candidate['cases_run'] === 'number' &&
    typeof candidate['passed'] === 'number' &&
    typeof candidate['failed'] === 'number' &&
    typeof candidate['rejected'] === 'number' &&
    typeof candidate['unavailable'] === 'number' &&
    typeof candidate['counted'] === 'number' &&
    (['proven', 'failed', 'insufficient_evidence', 'unproven'] as readonly unknown[]).includes(candidate['verdict']) &&
    typeof candidate['gates'] === 'boolean'
  );
}

export interface Bar {
  readonly minimum_cases: number;
  readonly minimum_pass_rate: number;
}

export function isBar(value: unknown): value is Bar {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['minimum_cases'] === 'number' &&
    typeof candidate['minimum_pass_rate'] === 'number'
  );
}

export interface CapabilityMatrix {
  readonly results: readonly CapabilityResult[];
  readonly bars: Readonly<Record<string, Bar>>;
  readonly gating_policy: string;
  readonly a_model_cannot_certify_itself: string;
}

export function isCapabilityMatrix(value: unknown): value is CapabilityMatrix {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['results']) &&
    (candidate['results'] as readonly unknown[]).every((item) => isCapabilityResult(item)) &&
    candidate['bars'] !== undefined &&
    typeof candidate['gating_policy'] === 'string' &&
    typeof candidate['a_model_cannot_certify_itself'] === 'string'
  );
}

export interface SuiteCapability {
  readonly code: Capability;
  readonly cases_here: number;
  /** What A04/A16 asks for. Compare with `cases_here`. */
  readonly cases_specified: number;
  readonly minimum_to_conclude: number;
  readonly minimum_pass_rate: number;
}

export function isSuiteCapability(value: unknown): value is SuiteCapability {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['pii_classification', 'rule_suggestion', 'anomaly_explanation', 'copilot_answer'] as readonly unknown[]).includes(candidate['code']) &&
    typeof candidate['cases_here'] === 'number' &&
    typeof candidate['cases_specified'] === 'number' &&
    typeof candidate['minimum_to_conclude'] === 'number' &&
    typeof candidate['minimum_pass_rate'] === 'number'
  );
}

export interface SuiteReport {
  readonly suite_version: string;
  readonly capabilities: readonly SuiteCapability[];
  readonly meets_specification: boolean;
  readonly note: string;
}

export function isSuiteReport(value: unknown): value is SuiteReport {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['suite_version'] === 'string' &&
    Array.isArray(candidate['capabilities']) &&
    (candidate['capabilities'] as readonly unknown[]).every((item) => isSuiteCapability(item)) &&
    typeof candidate['meets_specification'] === 'boolean' &&
    typeof candidate['note'] === 'string'
  );
}
